<template>
    <div class="border-gray-200 border-b">
        <div class="p-5 lg:py-24 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4 text-center">

            <template v-if="! rating || rating < 5 || ! submitted">
                <h3 class="mb-1 text-3xl font-medium">
                    How did we do?
                </h3>
                <h5 class="mb-4 lg:mb-16 text-base font-medium">
                    Please rate your experience with {{ consumerPortalData.consumerPortalName }}
                </h5>
            </template>
            <template v-else>
                <h3 class="mb-1 text-3xl font-medium">
                    Thank you for your business!
                </h3>
                <h5 class="mb-4 lg:mb-16 text-base font-medium">
                    Please click the “Write a review” button below, to leave us a review. It only takes a moment. We appreciate your feedback!
                </h5>
            </template>

            <div v-if="! rating" class="flex justify-center items-center">
                <a href="#" v-for="i in 5" :id="`star-${i}`" @mouseover="starHover(i)" @mouseleave="starLeave(i)" @click.prevent="setRating(i)" class="px-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="text-gray-300 w-10 h-10"
                         :class="{
                                'text-yellow-500': hover[i],
                                'text-gray-300': ! hover[i]
                            }">
                        <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
                    </svg>
                </a>
            </div>
            <template v-else>
                <div class="bg-yellow-50 border border-yellow-300 p-5">
                    <div class="flex justify-between">
                        <span :class="`text-${$root.theme}-500 text-sm font-medium`">Thank you for your feedback!</span>
                        <div class="flex items-center">
                            <svg v-for="i in 5" :id="`star-${i}`" @mouseover="starHover(i)" @mouseleave="starLeave(i)" @click.prevent="setRating(i)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6"
                                 :class="{
                                        'text-yellow-600': hover[i],
                                        'text-yellow-500': ! hover[i] && i <= rating,
                                        'text-gray-300': ! hover[i] && i > rating,
                                    }">
                                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
                            </svg>
                        </div>
                    </div>
                    <div v-if="$root.$refs.app.settings.feedback.collect_source" class="flex flex-col">
                        <label for="email" class="block font-medium mb-1 text-sm text-gray-700 text-left">
                            How did you hear from us?
                        </label>
                        <select v-model="source" class="border border-gray-200 p-2 mb-3 text-sm text-gray-800">
                            <option :value="null">Select One</option>
                            <option v-for="option in $root.$refs.app.settings.feedback.sources" v-text="option" />
                        </select>
                    </div>
                </div>
                <div v-if="! submitted" class="border border-gray-200 p-3 mt-5 flex justify-center">
                    <a href="#" id="feedback-submit-rating" @click.prevent="submitRating" class="bg-blue-500 text-white py-2 px-3 text-sm">
                        Submit Rating
                    </a>
                </div>
                <template v-else>
                    <div v-if="rating === 5" class="my-5">
                        <template v-if="linkOut && linkOut !== 'google-widget'">
                            <a :href="linkOut" target="_blank" class="py-2 px-3 bg-blue-500 text-white rounded">Click here to leave us a review!</a>
                        </template>
                        <div v-else :class="elfSightId" />
                    </div>
                    <div v-else class="border border-gray-200 p-3 my-5 flex flex-col justify-center">
                        <textarea v-model="feedback" id="feedback-textarea" class="border border-gray-200 p-3 mb-3 text-sm text-gray-800" placeholder="Please give us your feedback..." />

                        <div class="flex justify-center">
                            <a href="#" id="feedback-submit-feedback" @click.prevent="submitFeedback(true)" class="bg-blue-500 text-white py-2 px-3 text-sm">
                                Submit Feedback
                            </a>
                        </div>
                    </div>
                </template>
            </template>

            <a v-if="linkShown" :href="`${this.consumerPortalData.consumerPortalBaseUrl}/account`" class="w-full justify-center text-black underline inline-flex text-xs leading-4 font-medium">
                Take me to Account Summary
            </a>

        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import LinkOut from "../mixins/LinkOut";

    export default {
        mixins: [LinkOut],

        props: {
            accountId: {},
            email: {},
            referral: {},
            redirect: {},
            showLink: {
                type: Boolean,
                default: false
            },
            consumerPortalData: {}
        },

        mounted() {
            Vue.loadScript('https://apps.elfsight.com/p/platform.js');

            this.$root.$refs.app.updatePageName('obtain-feedback');
        },

        data() {
            return {
                linkShown: false,
                elfSight: window.elf_sight,
                elfSightId: window.elf_sight_id,
                rating: null,
                submitted: false,
                feedback: null,
                source: null,
                hover: {
                    1: false,
                    2: false,
                    3: false,
                    4: false,
                    5: false
                }
            };
        },

        methods: {
            starHover(nRating) {
                for (let i = 1; i <= 5; i++) {
                    this.hover[i] = i <= nRating;
                }
            },

            starLeave() {
                for (let i = 1; i <= 5; i++) {
                    this.hover[i] = false;
                }
            },

            setRating(numberOfStars) {
                if (! this.submitted) {
                    this.rating = numberOfStars;
                }
            },

            submitRating() {
                this.submitted = true;

                if (this.rating === 5 || ! this.elfSight) {
                    this.submitFeedback(false);
                }

                if (this.showLink) {
                    setTimeout(() => this.linkShown = true, 3000);
                }
            },

            /**
             * @param {Boolean} withRedirect
             * @returns {void}
             */
            submitFeedback(withRedirect) {
                this.$http.post(`${this.consumerPortalData.consumerPortalBaseUrl}/api/feedback/${this.accountId}`, {
                    referral: this.referral,
                    rating: this.rating,
                    feedback: this.feedback,
                    source: this.source,
                    link_out: this.linkOut
                }).then(() => {
                    if (withRedirect) {
                        window.location = this.redirect;
                    }
                });
            }
        }
    }
</script>
